/**
 * FormulaDisplay Component
 *
 * Renders a formula with human-readable variable names as clickable pills.
 * Transforms "{propertyValue} × (1 - {sellingCosts}/100)" into
 * "[Property Value] × (1 - [Selling Costs]/100)" with clickable pills.
 *
 * Hover behavior (recursive up to maxDepth):
 * - Assumptions: Show inline adjustment controls
 * - Computed: Show formula preview with nested pills
 */

import React, { useRef } from 'react';
import { useGraph } from '../GraphContext';
import { usePopup } from '../hooks';
import { isAssumptionNode, isComputedNode, isSourceNode } from '../../types';
import { InlineAdjust } from './InlineAdjust';
import { Popup } from './Popup';

export interface FormulaDisplayProps {
  formula: string;
  className?: string;
  /** Current depth level (used to limit recursion) */
  depth?: number;
  /** Maximum depth for hover popups (default 3) */
  maxDepth?: number;
  /** Optional navigation handler (for sidebar use - builds history) */
  onNavigate?: (nodeId: string) => void;
}

interface FormulaPart {
  type: 'text' | 'variable';
  content: string;
  nodeId?: string;
}

/**
 * Parse a formula string into parts (text and variable references)
 */
function parseFormula(formula: string): FormulaPart[] {
  const parts: FormulaPart[] = [];
  const regex = /\{([^}]+)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(formula)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: formula.slice(lastIndex, match.index),
      });
    }

    // Add the variable reference
    parts.push({
      type: 'variable',
      content: match[1], // The node ID
      nodeId: match[1],
    });

    lastIndex = regex.lastIndex;
  }

  // Add any remaining text
  if (lastIndex < formula.length) {
    parts.push({
      type: 'text',
      content: formula.slice(lastIndex),
    });
  }

  return parts;
}

/**
 * Variable pill component - clickable to inspect the node
 * Shows hover popups for assumptions (adjust) and computed (formula preview)
 */
/**
 * Add spacing around operators for better readability
 */
function formatTextPart(text: string): string {
  return text
    // Add space before / if not already there
    .replace(/([^\s])\//g, '$1 /')
    // Add space after / if not already there
    .replace(/\/([^\s])/g, '/ $1');
}

function VariablePill({
  nodeId,
  depth = 0,
  maxDepth = 3,
  onNavigate,
}: {
  nodeId: string;
  depth?: number;
  maxDepth?: number;
  onNavigate?: (nodeId: string) => void;
}): React.ReactElement {
  const ctx = useGraph();
  const node = ctx.getNode(nodeId);
  const value = ctx.getValue(nodeId);
  const label = node?.label || nodeId;
  const triggerRef = useRef<HTMLSpanElement>(null);

  // Use allowMultiple since pills appear inside other popups
  // and shouldn't close their parent when opening
  const popup = usePopup({ allowMultiple: true });

  const isAssumption = node && isAssumptionNode(node);
  const isComputed = node && isComputedNode(node);
  const isSource = node && isSourceNode(node);
  const atMaxDepth = depth >= maxDepth;

  // Use onNavigate if provided (sidebar mode), otherwise direct context call
  const handleClick = () => {
    if (onNavigate) {
      onNavigate(nodeId);
    } else {
      ctx.setInspectedNodeId(nodeId);
    }
  };

  return (
    <span
      ref={triggerRef}
      className="relative inline-block"
      {...popup.triggerProps}
    >
      <button
        onClick={handleClick}
        className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
      >
        {label}
      </button>

      {/* At max depth: show "click to view" */}
      {popup.isVisible && atMaxDepth && (
        <Popup isVisible {...popup.popupProps} minWidth="min-w-0" triggerRef={triggerRef}>
          <button
            onClick={handleClick}
            className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            Click to view →
          </button>
        </Popup>
      )}

      {/* Source: Show citation info */}
      {popup.isVisible && !atMaxDepth && isSource && (
        <Popup isVisible {...popup.popupProps} triggerRef={triggerRef}>
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Source</div>
          <div className="text-sm font-medium text-gray-900 mb-1">{node.citation.source}</div>
          {node.citation.description && (
            <div className="text-xs text-gray-600 mb-2">{node.citation.description}</div>
          )}
          {node.citation.url && (
            <a
              href={node.citation.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              View source
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </Popup>
      )}

      {/* Assumption: Show inline adjust */}
      {popup.isVisible && !atMaxDepth && isAssumption && value !== undefined && (
        <InlineAdjust
          nodeId={nodeId}
          node={node}
          value={value}
          onClose={popup.hide}
          onMouseEnter={popup.onPopupMouseEnter}
          onMouseLeave={popup.onPopupMouseLeave}
          triggerRef={triggerRef}
        />
      )}

      {/* Computed: Show formula preview with recursive pills */}
      {popup.isVisible && !atMaxDepth && isComputed && (
        <Popup isVisible {...popup.popupProps} triggerRef={triggerRef}>
          <div className="text-xs text-gray-500 mb-1">{label}</div>
          <div className="text-sm font-medium text-gray-900 mb-2">
            {value !== undefined ? ctx.formatValue(value, node.unit) : '—'}
          </div>
          <div className="text-xs text-gray-500 mb-1">Formula</div>
          <div className="text-xs text-gray-700">
            {/* Recursive formula display at next depth level */}
            <FormulaDisplay
              formula={node.formula}
              depth={depth + 1}
              maxDepth={maxDepth}
              onNavigate={onNavigate}
            />
          </div>
        </Popup>
      )}
    </span>
  );
}

export function FormulaDisplay({
  formula,
  className = '',
  depth = 0,
  maxDepth = 3,
  onNavigate,
}: FormulaDisplayProps): React.ReactElement {
  const parts = parseFormula(formula);

  return (
    <span className={`text-sm text-gray-700 inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 relative z-0 ${className}`}>
      {parts.map((part, index) => {
        if (part.type === 'variable' && part.nodeId) {
          return (
            <VariablePill
              key={index}
              nodeId={part.nodeId}
              depth={depth}
              maxDepth={maxDepth}
              onNavigate={onNavigate}
            />
          );
        }
        // Render text parts with proper spacing around operators
        return (
          <span key={index} className="text-gray-600 whitespace-nowrap">
            {formatTextPart(part.content)}
          </span>
        );
      })}
    </span>
  );
}

export default FormulaDisplay;
